import { Component, ElementRef, Input } from '@angular/core';
import { IMultiSelectOption } from 'angular-2-dropdown-multiselect';

import {
  DesignAttributeType,
  OptionsAttribute,
  AttributeChangeEventArgsType,
  AttributeReference
} from '../../../types/designer-attribute-type';
import { FormControl } from '../../../types/form-control.type';
import { BaseDesignerComponent } from '../base/base-designer-component';
import { DefaultLayout } from '../../main/completeContext';
import { SxfDesignerService } from '../../../services/sxfdesigner-service';
import { DesignerSelectItem } from '../../../types/product.type';

@Component({
  selector: 'sx-custom-control',
  templateUrl: './custom-control-designer.component.html'
})
export class SxCustomControl extends BaseDesignerComponent {
  @Input() customCode: string;

  private data: FormControl;
  optionsModel: string[];
  options: IMultiSelectOption[];
  esTipoTabla: boolean;
  isTypeList: boolean;

  constructor(
    container: ElementRef,
    private formularioDinamicoService: SxfDesignerService
  ) {
    super(container, 'SxCustomControl');
  }

  loadDefaults(): void {
    super.loadDefaults();
    this.getData();
    this.controlContext = this.data;

    if (this.data.descripcion) {
      this.controlContext.title = this.data.descripcion;
      this.controlContext.codAtributo = this.data.codAtributo;
      this.controlContext.path = this.data.path;

      this.esTipoTabla = this.getCustomeAttributeType() === 'TABLA';
      this.isTypeList = this.getCustomeAttributeType() === 'LIST';
    }

    this.options = [];
    const self = this;
    this.controlContext.extraInfos = this.controlContext.extraInfos || [];
    this.controlContext.extraInfos.forEach(item => {
      self.options.push({ id: item.value, name: item.desc });
    });
    if (this.isTypeList) {
      this.controlContext.numericCompare = 'NULL';
    }
  }

  private getData(): void {
    const externalControls = <Array<FormControl>>(
      JSON.parse(sessionStorage.externalControls)
    );
    this.data = externalControls.find(x => x.codAtributo === this.customCode);
  }

  getDesignerInfo(): any {
    const info = super.getDesignerInfo();

    info['codAtributo'] = this.controlContext.codAtributo;
    info['path'] = this.controlContext.path;
    info['title'] = this.controlContext.title;
    info['tabindex'] = this.controlContext.tabindex;

    if (this.isTypeList) {
      if (this.controlContext.layout == null) {
        this.controlContext.layout = DefaultLayout;
      }
      info['layout'] = this.controlContext.layout; // to initialize it not null
      info['allowAdd'] = this.controlContext.allowAdd;
      info['allowDelete'] = this.controlContext.allowDelete;
      info['maxRecords'] = this.controlContext.maxRecords; // to initialize it not null
      info['minRecords'] = this.controlContext.minRecords;
      info['duplication'] = this.controlContext.duplication;
      info['numericCompare'] = this.controlContext.numericCompare;
      if (this.controlContext.numericCompare != 'NULL') {
        info['compareTo'] = this.controlContext.compareTo;
        info['compareOver'] = this.controlContext.compareOver;
      }
    } else {
      info['layout'] = null; // to initialize it null if not list
    }

    info['validationGroup'] = this.controlContext.validationGroup;
    info['validators'] = this.controlContext.validators;
    info['required'] = this.controlContext.required;
    info['readOnly'] = this.controlContext.readOnly;
    info['spellcheck'] = this.controlContext.spellcheck;

    // Si el atributo es TABLA_SIMPLE, obtenemos el valor de 'isRadioList'
    if (this.getCustomeAttributeType() === 'TABLA_SIMPLE') {
      info['multiple'] = this.controlContext.multiple;
      info['isRadioList'] = this.controlContext.isRadioList;
      info['isPreloaded'] = this.controlContext.isPreloaded;
      info['isPreloader'] = this.controlContext.isPreloader;
      info['source'] = this.controlContext.source;
    }

    // Si el atributo es TABLA, obtenemos el valor de 'tableStyle'
    if (this.getCustomeAttributeType() === 'TABLA') {
      info['multiple'] = this.controlContext.multiple;
      info['tableStyle'] = this.controlContext.tableStyle;
      info['isPreloaded'] = this.controlContext.isPreloaded;
      info['isPreloader'] = this.controlContext.isPreloader;
    }
    return info;
  }

  buildAttributes(): void {
    super.buildAttributes();
    this.esTipoTabla = this.getCustomeAttributeType() === 'TABLA';

    const inputTypeOptions = [
      { id: 'date', name: 'Custom Date' },
      { id: 'text', name: 'Text' }
    ];

    const spellcheckAtt = new DesignAttributeType(
      this.controlContext,
      'boolean',
      'spellcheck',
      'Check spelling'
    );
    const codAtributoAtt = new DesignAttributeType(
      this.controlContext,
      'readonly',
      'codAtributo',
      'Attribute Code'
    );
    const titleAtt = new DesignAttributeType(
      this.controlContext,
      'string',
      'title',
      'Title'
    );
    const validationGroupAtt = new DesignAttributeType(
      this.controlContext,
      'string',
      'validationGroup',
      'Validation Group'
    );
    const validatorsAtt = new DesignAttributeType(
      this.controlContext,
      'richtext',
      'validators',
      'Validators'
    );
    const requiredAtt = new DesignAttributeType(
      this.controlContext,
      'boolean',
      'required',
      'Required'
    );
    const readOnlyAtt = new DesignAttributeType(
      this.controlContext,
      'boolean',
      'readOnly',
      'Read Only'
    );
    const isRadioList = new DesignAttributeType(
      this.controlContext,
      'boolean',
      'isRadioList',
      'Radio List'
    );
    const isPreloaded = new DesignAttributeType(
      this.controlContext,
      'boolean',
      'isPreloaded',
      'Preloaded'
    );
    const isPreloader = new DesignAttributeType(
      this.controlContext,
      'boolean',
      'isPreloader',
      'Preloader'
    );
    const inputType = new OptionsAttribute(
      this.controlContext,
      inputTypeOptions,
      'inputType',
      'SubType'
    );
    const tabindex = new DesignAttributeType(
      this.controlContext,
      'tabindex',
      'tabindex',
      'Tabindex'
    );

    this.attributes.push(
      codAtributoAtt,
      titleAtt,
      validationGroupAtt,
      validatorsAtt,
      tabindex
    );

    if(this.getCustomeAttributeType() === 'String')
      this.attributes.push(inputType);

    // Si el atributo es TABLA, mostramos el select de 'tableStyle'
    if (this.getCustomeAttributeType() === 'TABLA') {
      const styles = [
        { id: 'combo', name: 'Combo' },
        { id: 'autocomplete', name: 'Autocomplete' },
        { id: 'showAll', name: 'Show All' },
        { id: 'multipleSelector', name: 'MultipleSelector' }
      ];
      const tableStyleAtt = new OptionsAttribute(
        this.controlContext,
        styles,
        'tableStyle',
        'Table Style'
      );
      tableStyleAtt.change.subscribe(value =>
        this.onTableStyleAttChange(value)
      );

      const source = new DesignAttributeType(
        this.controlContext,
        'string',
        'source',
        'Source'
      );

      source.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];

      const triggerMinLength = new DesignAttributeType(
        this.controlContext,
        'string',
        'triggerMinLength',
        'Search Length'
      );

      triggerMinLength.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];

      const searchConditions = [
        { id: 'contains', name: 'Contains' },
        { id: 'startWith', name: 'Starts with' },
      ];

      const searchType = new OptionsAttribute(
        this.controlContext,
        searchConditions,
        'searchType',
        'Search Type'
      );

      searchType.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];

      
      const documentsAttr = this.designerContext.externalControls.map(({ codAtributo, descripcion }) => ({id: codAtributo, name:descripcion }));
    
      const description = new OptionsAttribute(
        this.controlContext,
        documentsAttr,
        'attributeValueLabel',
        'Attr Value Label'
      );

      description.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];

      const changeOnBlur = new DesignAttributeType(
        this.controlContext,
        'boolean',
        'changeOnBlur',
        'Trigger change on lost focus'
      );

      changeOnBlur.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];
  
      const placeHolder = new DesignAttributeType(
        this.controlContext,
        'string',
        'placeHolder',
        'Place Holder'
      );

      placeHolder.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];

      const serverSide = new DesignAttributeType(
        this.controlContext,
        'boolean',
        'isServerSide',
        'Server Side'
      );

      serverSide.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];

      this.attributes.push(tableStyleAtt, isPreloader, isPreloaded, source, triggerMinLength, placeHolder, serverSide,searchType, description, changeOnBlur);
    }

    // Si el atributo es TABLA_SIMPLE, mostramos el check de 'isRadioList'
    if (this.getCustomeAttributeType() === 'TABLA_SIMPLE') {
      const styles = [
        { id: 'showAll', name: 'Show All' },
        { id: 'autocomplete', name: 'Autocomplete' },
        { id: 'multipleSelector', name: 'MultipleSelector' }
      ];
      const tableStyleAtt = new OptionsAttribute(
        this.controlContext,
        styles,
        'tableStyle',
        'Table Style'
      );

      const source = new DesignAttributeType(
        this.controlContext,
        'string',
        'source',
        'Source'
      );
      source.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];

      const triggerMinLength = new DesignAttributeType(
        this.controlContext,
        'string',
        'triggerMinLength',
        'Search Length'
      );

      const searchConditions = [
        { id: 'contains', name: 'Contains' },
        { id: 'startWith', name: 'Start with' },
      ];

      const searchType = new OptionsAttribute(
        this.controlContext,
        searchConditions,
        'searchType',
        'Search Type'
      );

      searchType.hiddenConditions = [['tableStyle', '!=', 'autocomplete']];


      this.attributes.push(
        tableStyleAtt,
        source,
        isRadioList,
        isPreloader,
        isPreloaded,
        triggerMinLength,
        searchType
      );
    }

    this.attributes.push(requiredAtt, readOnlyAtt,spellcheckAtt);

    const layout = new DesignAttributeType(
      this.controlContext,
      'string',
      'layout',
      'Layout'
    );
    this.attributes.push(layout);

    if (this.getCustomeAttributeType() === 'LIST') {
      const compareTypes = [
        { id: 'NULL', name: 'NONE' }, // null
        { id: 'LT', name: ' <  ' }, // less than
        { id: 'GT', name: ' >  ' }, // greater than
        { id: 'LE', name: ' <= ' }, // less equal
        { id: 'GE', name: ' >= ' }, // greater equal
        { id: 'EQ', name: ' == ' }, // equal
        { id: 'NE', name: ' != ' } // non equal
      ];

      const numericCompare = new OptionsAttribute(
        this.controlContext,
        compareTypes,
        'numericCompare',
        'Number compare validate'
      );
      const compareTo = new AttributeReference(
        this.controlContext,
        this.designerContext.product.attributeCodes,
        'compareTo',
        'Compate To'
      );
      compareTo.hiddenConditions = [['numericCompare', '===', 'NULL']];
      const compareOver = new AttributeReference(
        this.controlContext,
        [],
        'compareOver',
        'Compare Over'
      );
      compareOver.hiddenConditions = [['numericCompare', '===', 'NULL']];
      const compareMessage = new DesignAttributeType(
        this.controlContext,
        'string',
        'compareMessage',
        'Compare Message'
      );
      compareMessage.hiddenConditions = [['numericCompare', '===', 'NULL']];

      const allowDelete = new DesignAttributeType(
        this.controlContext,
        'boolean',
        'allowDelete',
        'Allow delete'
      );
      const allowAdd = new DesignAttributeType(
        this.controlContext,
        'boolean',
        'allowAdd',
        'Allow add'
      );
      const maxRecords = new DesignAttributeType(
        this.controlContext,
        'string',
        'maxRecords',
        'Maximum records'
      );
      const minRecords = new DesignAttributeType(
        this.controlContext,
        'string',
        'minRecords',
        'Minimum records'
      );

      const duplication = new DesignAttributeType(
        this.controlContext,
        'string',
        'duplication',
        'Unique fields'
      );
      const dupplicateMessage = new DesignAttributeType(
        this.controlContext,
        'string',
        'dupplicateMessage',
        'Message for duplications'
      );
      dupplicateMessage.hiddenConditions = [['duplication', '==', '']];
      dupplicateMessage.hiddenConditions = [['duplication', '==', undefined]];

      this.attributes.push(allowDelete);
      this.attributes.push(allowAdd);
      this.attributes.push(maxRecords);
      this.attributes.push(minRecords);
      this.attributes.push(duplication);
      this.attributes.push(dupplicateMessage);
      this.attributes.push(numericCompare);
      this.attributes.push(compareTo);
      this.attributes.push(compareOver);
      this.attributes.push(compareMessage);

      this.fixAttributeList();
    }

    const path = new DesignAttributeType(
      this.controlContext,
      'string',
      'path',
      'Path'
    );
    this.attributes.push(path);
  }

  onTableStyleAttChange(args: AttributeChangeEventArgsType): void {
    this.controlContext.tableStyle = args.newValue;
  }

  onNumericComparaValidateChange(args: AttributeChangeEventArgsType): void {
    this.controlContext.numericCompare = args.newValue;
  }

  // Funcion para saber el tipo del custom attribute
  // Ej: Si el type es 'SxCustomControl::CLIENTE_APELLIDO::String'
  // retornaria 'String'
  getCustomeAttributeType() {
    const splitName = this.type.split('::');
    return splitName[2];
  }

  fixAttributeList(): void {
    const contexto = this.designerContext.product;

    this.formularioDinamicoService
      .getCustomAtributos(
        contexto.codSistema,
        contexto.codNegocio,
        contexto.codProducto,
        contexto.codServicio,
        contexto.codComando,
        contexto.documentKind,
        contexto.standardkey,
        null,
        this.controlContext.path
      )
      .then(atributos => {
        let desAttrs = [];
        (atributos.formControls as any[]).forEach(element => {
          const design: DesignerSelectItem = {
            id: element.codAtributo,
            description: element.codAtributo
          };
          desAttrs.push(design);
        });
        (this.attributes.find(
          x => x.name === 'compareOver'
        ) as AttributeReference).options = desAttrs;
      });
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/private/sxfdesigner/components/designers/custom/custom-control-designer.component.ts